import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorMode,
  useColorModeValue,
  useToast,
  VStack
} from '@chakra-ui/react';
import CryptoJS from 'crypto-js';
import { useWillChange } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate } from 'react-router';

import { createMachine } from '../../api/postApi';
import { COMMON_ALT, COMMON_WEB_PATH } from '../../constant';

const CreateMachine = () => {
  const { t } = useTranslation();
  const i18n = (key, props) => t(`machine.${key}`, { ...props });
  const i18n_common = (key) => t(`common.${key}`);

  const [formData, setFormData] = useState({
    cagent_account: '',
    cagent_password: '',
    mac_address: '',
    area: '',
    number: ''
  });

  const willChange = useWillChange();

  const { colorMode, toggleColorMode } = useColorMode();
  const boxBg = useColorModeValue('light.200', 'dark.200');
  const boxLightBg = useColorModeValue('light.100', 'dark.100');
  const textColor = useColorModeValue('dark.100', 'light.100');

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const toast = useToast();

  //處理URL參數
  useEffect(() => {
    // 取URL參數
    const urlParams = new URLSearchParams(window.location.search);
    const macAddress = urlParams.get('mac');

    // 如果有mac參數，更新表單數據
    if (macAddress) {
      setFormData((prev) => ({
        ...prev,
        mac_address: macAddress
      }));
    }
  }, []); // 空依賴 只在組件掛載時執行一次

  const handleSeePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const encryptedFormData = {
        ...formData,
        cagent_password: CryptoJS.MD5(formData.cagent_password).toString()
      };
      const response = await createMachine({ formData: encryptedFormData });

      // 檢查response本身而不是response.data
      if (response?.success === true) {
        // 檢查 success 是否為 true
        toast({
          title: i18n('createSuccess'),
          status: 'success',
          duration: 3000,
          isClosable: true
        });
        // 創建成功後跳轉到首頁
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    } catch (error) {
      console.log('創建機台失敗:', error.response?.data?.message);
      // toast({
      //   title: i18n("createError"),
      //   description: error.response?.data?.message,
      //   status: "error",
      //   duration: 3000,
      //   isClosable: true,
      // });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      zIndex={9999}
      backdropBlur="md"
      position="fixed"
      top="0px"
      left="0px"
      w="100%"
      h="100%"
      display={'flex'}
      alignItems={'center'}
      justifyContent={'center'}
    >
      <Box
        style={{ willChange }}
        w="100%"
        h="100%"
        overflowY="scroll"
        pb={'25px'}
        pt={'10px'}
        px={'15px'}
        borderRadius={'lg'}
        maxW={'450px'}
        bg={boxBg}
        color={textColor}
      >
        <Flex
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={`center`}
          gap={4}
          mb={6}
        >
          <Image
            src={`${COMMON_WEB_PATH}/blue-layout/logo_192.png`}
            width={'120px'}
            alt={COMMON_ALT}
          />
          <Text fontSize="3xl" fontWeight={'bold'} textAlign={'center'}>
            {i18n('createMachine')}
          </Text>
        </Flex>
        <form onSubmit={handleSubmit}>
          <VStack spacing="4">
            <FormControl isRequired>
              <FormLabel>{i18n('cagentAccount')}</FormLabel>
              <Input
                name="cagent_account"
                value={formData.cagent_account}
                onChange={handleChange}
                placeholder={i18n('enterCagentAccount')}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>{i18n('cagentPassword')}</FormLabel>
              <InputGroup>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="cagent_password"
                  value={formData.cagent_password}
                  onChange={handleChange}
                  placeholder={i18n('enterCagentPassword')}
                />
                <InputRightElement>
                  {showPassword ? (
                    <FaEyeSlash
                      className="cursor-pointer"
                      onClick={handleSeePassword}
                    />
                  ) : (
                    <FaEye
                      className="cursor-pointer"
                      onClick={handleSeePassword}
                    />
                  )}
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>{i18n('macAddress')}</FormLabel>
              <Input
                name="mac_address"
                value={formData.mac_address}
                onChange={handleChange}
                placeholder={i18n('enterMacAddress')}
                readOnly
                _hover={{ cursor: 'not-allowed' }} // 禁止滑鼠hover
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>{i18n('macArea')}</FormLabel>
              <Input
                name="area"
                value={formData.area}
                onChange={handleChange}
                placeholder={i18n('enterMacArea')}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>{i18n('macNumber')}</FormLabel>
              <Input
                name="number"
                value={formData.number}
                onChange={handleChange}
                placeholder={i18n('enterMacNumber')}
              />
            </FormControl>

            <Button
              type="submit"
              variant={`brandPrimary`}
              bg="brand.500"
              fontWeight={`bold`}
              color={`white`}
              minWidth={`55px`}
              fontSize="md"
              width="full"
              mt="4"
              isLoading={isLoading}
              disabled={isLoading}
            >
              {i18n('submit')}
            </Button>
          </VStack>
        </form>
      </Box>
    </Box>
  );
};

export default CreateMachine;
